import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

/**
 * The signed-in fan's own Top Fans name (Nicky, 27 Oct 2026), from
 * my_leaderboard_name(). Everything here is hidden unless `live` is true, which
 * the server sets from the leaderboard_names switch for this viewer.
 */
export interface MyLeaderboardName {
  available: boolean;
  /** the new names are on for this fan */
  live?: boolean;
  /** what the fan shows as right now, their initials choice included */
  shown?: string | null;
  /** what they show as without the choice */
  name?: string | null;
  initials?: string | null;
  use_initials?: boolean;
  /** one of the nicknames; a busy list may give them the next one */
  is_nickname?: boolean;
  /** built for them rather than their own first name */
  generated?: boolean;
  /** they've seen the one-time "You show up on Top Fans as ..." notice */
  seen?: boolean;
}

export function isLeaderboardNameLive(state: MyLeaderboardName | null): state is MyLeaderboardName {
  return !!state?.available && !!state.live;
}

export function useMyLeaderboardName(userId: string | null | undefined) {
  const [state, setState] = useState<MyLeaderboardName | null>(null);
  // Bumped when a Supabase session starts or ends. The bio page can know the fan
  // (from a saved email) before they've signed in, when the owner-only RPC
  // refuses; signing in then has to fetch again.
  const [sessionVersion, setSessionVersion] = useState(0);

  useEffect(() => {
    const auth = supabase.auth;
    if (!auth?.onAuthStateChange) return undefined;
    const { data } = auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        setSessionVersion((v) => v + 1);
      }
    });
    return () => data?.subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState(null);
    if (!userId) return undefined;
    (async () => {
      const { data, error } = await supabase.rpc('my_leaderboard_name');
      if (cancelled || error || !data) return;
      setState(data as MyLeaderboardName);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, sessionVersion]);

  /** Switches initials on or off; resolves false when the server refused. */
  const setUseInitials = useCallback(async (on: boolean) => {
    const { data, error } = await supabase.rpc('set_my_leaderboard_initials', { p_on: on });
    if (error || !data) return false;
    setState(data as MyLeaderboardName);
    return true;
  }, []);

  /** Remembers the one-time notice server-side (every device). */
  const markSeen = useCallback(async () => {
    setState((prev) => (prev ? { ...prev, seen: true } : prev));
    await supabase.rpc('mark_my_leaderboard_name_seen');
  }, []);

  return { state, setUseInitials, markSeen };
}

/** "(you)" goes on a leaderboard row only for the signed-in viewer's own row, and only when live. */
export function isViewerRow(rowUserId: string | null | undefined, viewerId: string | null | undefined, state: MyLeaderboardName | null): boolean {
  return isLeaderboardNameLive(state) && !!viewerId && !!rowUserId && rowUserId === viewerId;
}

/** The one-time notice after sign-in, or null when there's nothing new to tell them. */
export function topFansNameNotice(state: MyLeaderboardName | null): string | null {
  if (!isLeaderboardNameLive(state) || state.seen || !state.generated) return null;
  const shown = (state.shown || state.name || '').trim();
  if (!shown) return null;
  // "Bob R." and "B.R." already end the sentence
  return `You show up on Top Fans as ${shown}${shown.endsWith('.') ? '' : '.'}`;
}
